import { useMemo } from 'react';
import { startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear } from 'date-fns';
import type { Account } from '@/types/base';

interface UseDashboardMetricsProps {
  transactions: any[];
  accounts: Account[];
  convertToDefault: (amount: number, currency: string) => number;
  defaultCurrency: string;
  areCurrenciesLoaded: boolean;
  selectedPeriod: string;
}

export const useDashboardMetrics = ({
  transactions,
  accounts,
  convertToDefault,
  defaultCurrency,
  areCurrenciesLoaded,
  selectedPeriod
}: UseDashboardMetricsProps) => {
  
  const metrics = useMemo(() => {
    if (!Array.isArray(transactions) || !areCurrenciesLoaded) {
      return {
        dailyAvgSpending: 0,
        monthOverMonthChange: 0,
        savingsRate: 0,
        isIncrease: false
      };
    }

    const now = new Date();
    
    // Get date ranges based on selected period
    const getDateRanges = () => {
      switch (selectedPeriod) {
        case 'thisMonth':
          return {
            currentStart: startOfMonth(now),
            currentEnd: endOfMonth(now),
            prevStart: startOfMonth(subMonths(now, 1)),
            prevEnd: endOfMonth(subMonths(now, 1))
          };
        case 'lastMonth':
          const lastMonth = subMonths(now, 1);
          return {
            currentStart: startOfMonth(lastMonth),
            currentEnd: endOfMonth(lastMonth),
            prevStart: startOfMonth(subMonths(lastMonth, 1)),
            prevEnd: endOfMonth(subMonths(lastMonth, 1))
          };
        case 'last3Months':
          return {
            currentStart: startOfMonth(subMonths(now, 2)),
            currentEnd: endOfMonth(now),
            prevStart: startOfMonth(subMonths(now, 5)),
            prevEnd: endOfMonth(subMonths(now, 3))
          };
        case 'last6Months':
          return {
            currentStart: startOfMonth(subMonths(now, 5)),
            currentEnd: endOfMonth(now),
            prevStart: startOfMonth(subMonths(now, 11)),
            prevEnd: endOfMonth(subMonths(now, 6))
          };
        case 'thisYear':
          return {
            currentStart: startOfYear(now),
            currentEnd: endOfYear(now),
            prevStart: startOfYear(subMonths(now, 12)),
            prevEnd: endOfYear(subMonths(now, 12))
          };
        case 'allTime':
          return {
            currentStart: new Date(0), // Beginning of time
            currentEnd: now,
            prevStart: new Date(0),
            prevEnd: subMonths(now, 12) // Compare to previous year for trend
          };
        default:
          return {
            currentStart: startOfMonth(now),
            currentEnd: endOfMonth(now),
            prevStart: startOfMonth(subMonths(now, 1)),
            prevEnd: endOfMonth(subMonths(now, 1))
          };
      }
    };

    const { currentStart, currentEnd, prevStart, prevEnd } = getDateRanges();

    // Daily average spending for the current period
    const periodTransactions = transactions.filter(t => 
      t.type === 'expense' && 
      new Date(t.date) >= currentStart && 
      new Date(t.date) <= currentEnd
    );

    const totalPeriodExpenses = periodTransactions.reduce((sum, transaction) => {
      const account = accounts.find((a: Account) => a.id === transaction.account_id);
      if (!account) return sum;
      
      const amount = typeof transaction.amount === 'string' ? 
        parseFloat(transaction.amount) : transaction.amount;
      
      if (isNaN(amount)) return sum;
      
      const sourceCurrency = account.currency || transaction.currency || defaultCurrency;
      
      try {
        const convertedAmount = convertToDefault(Math.abs(amount), sourceCurrency);
        return sum + convertedAmount;
      } catch (error) {
        console.error('Error converting period expense:', error);
        return sum + Math.abs(amount);
      }
    }, 0);

    // Calculate daily average based on period length
    const periodDays = Math.max(1, Math.ceil((currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24)));
    const dailyAvgSpending = totalPeriodExpenses / periodDays;

    // Current period expenses
    const currentPeriodExpenses = totalPeriodExpenses;

    // Previous period expenses for comparison
    const prevPeriodExpenses = transactions
      .filter(t => t.type === 'expense' && 
        new Date(t.date) >= prevStart && 
        new Date(t.date) <= prevEnd)
      .reduce((sum, transaction) => {
        const account = accounts.find((a: Account) => a.id === transaction.account_id);
        if (!account) return sum;
        
        const amount = typeof transaction.amount === 'string' ? 
          parseFloat(transaction.amount) : transaction.amount;
        
        if (isNaN(amount)) return sum;
        
        const sourceCurrency = account.currency || transaction.currency || defaultCurrency;
        
        try {
          const convertedAmount = convertToDefault(Math.abs(amount), sourceCurrency);
          return sum + convertedAmount;
        } catch (error) {
          console.error('Error converting prev period expense:', error);
          return sum + Math.abs(amount);
        }
      }, 0);

    // Period over period change
    const monthOverMonthChange = prevPeriodExpenses === 0 ? 
      0 : 
      ((currentPeriodExpenses - prevPeriodExpenses) / prevPeriodExpenses) * 100;
    
    const isIncrease = monthOverMonthChange > 0;

    // Period income for savings rate
    const periodIncome = transactions
      .filter(t => t.type === 'income' && 
        new Date(t.date) >= currentStart && 
        new Date(t.date) <= currentEnd)
      .reduce((sum, transaction) => {
        const account = accounts.find((a: Account) => a.id === transaction.account_id);
        if (!account) return sum;
        
        const amount = typeof transaction.amount === 'string' ? 
          parseFloat(transaction.amount) : transaction.amount;
        
        if (isNaN(amount)) return sum;
        
        const sourceCurrency = account.currency || transaction.currency || defaultCurrency;
        
        try {
          const convertedAmount = convertToDefault(Math.abs(amount), sourceCurrency);
          return sum + convertedAmount;
        } catch (error) {
          console.error('Error converting period income:', error);
          return sum + Math.abs(amount);
        }
      }, 0);

    const savingsRate = periodIncome === 0 ? 
      0 : 
      ((periodIncome - currentPeriodExpenses) / periodIncome) * 100;

    return {
      dailyAvgSpending,
      monthOverMonthChange,
      savingsRate,
      isIncrease
    };
  }, [transactions, accounts, convertToDefault, defaultCurrency, areCurrenciesLoaded, selectedPeriod]);

  return metrics;
}; 